import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Copy } from 'lucide-react';
import { useCreateInvitation } from '@/hooks/use-invitations';
import { toast } from 'sonner';
import type { InvitationResponse } from '@/types/invitation';

export function CreateInvitationDialog() {
  const [open, setOpen] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState('7');
  const [createdInvitation, setCreatedInvitation] = useState<InvitationResponse | null>(null);
  const mutation = useCreateInvitation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const days = Number(expiresInDays);
    mutation.mutate(
      days > 0 ? { expires_in_days: days } : undefined,
      {
        onSuccess: (data) => {
          setCreatedInvitation(data);
          toast.success('Invitation code created');
        },
        onError: () => toast.error('Failed to create invitation'),
      },
    );
  };

  const handleClose = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setCreatedInvitation(null);
      setExpiresInDays('7');
    }
  };

  const copyLink = () => {
    const text = createdInvitation?.link || createdInvitation?.code || '';
    navigator.clipboard.writeText(text);
    toast.success(createdInvitation?.link ? 'Link copied to clipboard' : 'Code copied to clipboard');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger render={<Button />}>
        <Plus className="mr-2 h-4 w-4" />
        Create Invitation
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {createdInvitation ? 'Invitation Created' : 'Create Invitation Code'}
          </DialogTitle>
        </DialogHeader>
        {createdInvitation ? (
          <div className="space-y-4">
            {createdInvitation.link ? (
              <div className="space-y-2">
                <Label>Share this link with your student</Label>
                <div className="flex gap-2">
                  <Input value={createdInvitation.link} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={copyLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Invitation Code</Label>
                <div className="flex gap-2">
                  <Input value={createdInvitation.code} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={copyLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            <Button className="w-full" onClick={() => handleClose(false)}>
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="expiresInDays">Expires in (days)</Label>
              <Input
                id="expiresInDays"
                type="number"
                min="1"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
